import { useState, useEffect, useRef } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

import Navbar from "./Navbar";
import Footer from "./Footer";

import bapc from "../images/bacp.png";
import service from "../images/service.png";
import profile from "../images/profile.png";
import bg from "../images/bg.jpeg";

const faqs = [
  {
    id: "faq1",
    q: "How do I get started?",
    a: "I offer a 20-minute warm, no pressure initial consultation where we can simply talk, get to know each other, and determine whether we would be a good fit to work together. I'll guide you through the next steps and answer any questions about therapy, fees, and availability. There is no obligation and no expectation. Just a conversation.",
  },
  {
    id: "faq2",
    q: "How long does therapy last?",
    a: "Each session is 50 minutes. The overall length of therapy varies from person to person. Some people feel ready after a handful of sessions, while others prefer ongoing support over a longer period. We'll check in regularly and move at a pace that feels right for you.",
  },
  {
    id: "faq3",
    q: "Is everything I share kept confidential?",
    a: "Yes. What you share in our sessions remains private. As a BACP registered counsellor I follow their Ethical Framework, which means confidentiality is taken seriously. There are rare legal exceptions. For example, if I believe there is a risk to your life or someone else's and I will always discuss these limits with you at the start.",
  },
  {
    id: "faq4",
    q: "What if I need to cancel or reschedule?",
    a: "Life happens. I understand. I simply ask for at least 48 hours notice if you need to cancel or move a session. Late cancellations or missed sessions may be charged at the full session fee, but I will always approach this with understanding and flexibility.",
  },
];

const hearAboutOptions = [
  "Google search",
  "Counselling directory",
  "BACP website",
  "Recommendation",
  "Instagram",
  "Facebook",
  "Other",
];

const supportItems = [
  "Anxiety & overwhelm",
  "Low mood",
  "Low self-esteem",
  "Relationships",
  "Life transitions",
  "Feeling lost or purposeless",
  "Stress & distress",
  "Forgiveness",
  "Guilt and shame",
  "Infidelity",
];

const testimonials = [
  "Dora has a gentle yet clear way of helping me untangle my thoughts around anxiety, attachment, and relationship triggers. She doesn't dismiss my fears. She helps me understand where they come from. I leave sessions feeling both challenged and supported. I'm truly grateful for the clarity and steadiness she brings.",
  "I love how our sessions are interactive and not just me talking. The activities are really interesting and insightful!",
  "My experience with Dora has been incredibly supportive. Her guidance helped me navigate things with more clarity and compassion for myself. Thank you for the work we've done together.",
];

const btn =
  "inline-block px-7 py-[0.875rem] bg-[#d9a299] text-white font-semibold text-[0.82rem] tracking-[0.08em] uppercase rounded-[2px] cursor-pointer no-underline hover:bg-[#c98e86] transition-colors border-none leading-none";

const eyebrow =
  "block text-[0.67rem] tracking-[0.25em] uppercase text-[#b07068] font-semibold mb-6";

const sectionTitle =
  "font-lora text-[clamp(1.5rem,2.5vw,2rem)] font-bold text-[#3a3a3a] mt-2 mb-5 leading-[1.25]";

function Home() {
  const sectionsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    sectionsRef.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRef = (el: HTMLElement | null) => {
    if (el && !sectionsRef.current.includes(el)) sectionsRef.current.push(el);
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [hearAbout, setHearAbout] = useState<string[]>([]);
  const [hearAboutError, setHearAboutError] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const toggleHearAbout = (option: string) => {
    setHearAbout((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option],
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);
    if (hearAbout.length === 0) {
      setHearAboutError(true);
      return;
    }
    setHearAboutError(false);
    try {
      await addDoc(collection(db, "mail"), {
        to: "nymfodora23@gmail.com",
        message: {
          subject: `New Message from Your Website`,
          text: `You have a new message from your website contact form.\n\nName: ${name}\nEmail: ${email}\nMessage: ${message}\nHeard about me via: ${hearAbout.join(", ")}`,
          html: `<p>You have a new message from your website contact form:</p>
                 <p><strong>Name:</strong> ${name}</p>
                 <p><strong>Email:</strong> ${email}</p>
                 <p><strong>Message:</strong> ${message}</p>
                 <p><strong>Heard about me via:</strong> ${hearAbout.join(", ")}</p>`,
        },
        createdAt: serverTimestamp(),
      });
      setName("");
      setEmail("");
      setMessage("");
      setHearAbout([]);
      setStatus("success");
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  };

  const inputCls =
    "w-full border border-[#e0d5d0] rounded-[2px] px-4 py-[0.85rem] bg-white text-[0.95rem] text-[#3a3a3a] outline-none focus:border-[#d9a299] transition-colors";
  const labelCls =
    "block text-[0.72rem] tracking-[0.12em] uppercase font-semibold text-[#7a7a7a] mb-2";

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* Olive tree background */}
      <img
        src={bg}
        alt=""
        className="fixed top-1/2 z-0 pointer-events-none w-[85%] right-[-15%] opacity-[0.18] md:w-[48%] md:right-[-5%] md:opacity-[0.28]"
        style={{ transform: "translateY(-50%) scaleX(-1)" }}
      />

      <div className="relative z-[1]">
        <Navbar />

        {/* ── HERO ── */}
        <section
          id="about"
          ref={addRef}
          className="fade-up max-w-4xl mx-auto px-6"
        >
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-center min-h-[88vh]">
            <div className="col-span-12 md:col-span-7 py-16">
              {/* Desktop: eyebrow above name */}
              <span className={`hidden md:block ${eyebrow}`}>
                Practising Online · MBACP Registered
              </span>
              <img
                src={profile}
                alt="Nymfodora Giannakidou"
                className="md:hidden w-[96px] h-[96px] rounded-full object-cover object-top mb-5 border-2 border-[rgba(217,162,153,0.4)]"
              />
              <h1 className="font-lora text-[clamp(2.2rem,4vw,3.5rem)] font-bold leading-[1.08] text-[#3a3a3a] tracking-tight mb-4">
                Nymfodora
                <br />
                Giannakidou
              </h1>
              {/* Mobile: eyebrow below name */}
              <span className={`md:hidden ${eyebrow} mb-7`}>
                Practising Online · MBACP Registered
              </span>
              <p className="text-[#6b6b6b] max-w-[460px] leading-[1.85]">
                Life can feel overwhelming, confusing and exhausting, and
                sometimes we carry more than we ever let anyone see. If you have
                found yourself here, something in you is already reaching toward
                something different. That takes courage. At the heart of my
                practice is a deep and genuine belief that every person deserves
                a space where they feel truly seen, heard and accepted.
              </p>
              <div className="border-t border-[rgba(217,162,153,0.45)] my-9 w-[72px]" />
              <a href="#contact" className={btn}>
                Book a Free Consultation
              </a>
            </div>

            <div className="hidden md:block md:col-span-5">
              <img
                src={profile}
                alt="Nymfodora Giannakidou"
                className="w-full object-cover object-top rounded-[3px]"
                style={{ aspectRatio: "3/4" }}
              />
            </div>
          </div>
        </section>

        {/* ── AT THE HEART OF IT ── */}
        <section
          ref={addRef}
          className="fade-up bg-[rgba(240,228,211,0.4)] py-20 mt-20"
        >
          <div className="max-w-4xl mx-auto px-6">
            <span className={eyebrow}>A Little About Me</span>
            <h2 className={sectionTitle}>At the Heart of It</h2>
            <div className="max-w-[640px] flex flex-col gap-5">
              <p className="text-[#6b6b6b] leading-[1.8]">
                I have always been drawn to the parts of people that rarely
                get to speak, the feelings that sit just beneath the surface,
                the stories that have never quite been told out loud.
              </p>
              <p className="text-[#6b6b6b] leading-[1.8]">
                I came to this work not from a distance, but from the inside.
                Like many of the people I sit with, I know what it is to carry
                things quietly, to feel deeply and not always know what to do
                with that. My own experience of therapy was one of the most
                significant journeys of my life, it gave me the courage to
                stop performing and start inhabiting myself.
              </p>
              <p className="text-[#6b6b6b] leading-[1.8]">
                I chose this work because I believe in what becomes possible
                when a person is truly met without judgement, without agenda,
                without being rushed toward an answer. I am endlessly curious
                about people, about the complexity of the inner world and the
                resilience that exists even in the most exhausted of us.
              </p>
              <p className="text-[#6b6b6b] leading-[1.8]">
                This work is not about fixing anyone. It is about creating a
                space where you can hear yourself think, feel what you have
                been avoiding feeling, and slowly begin to find your way back
                to who you actually are.
              </p>
            </div>
          </div>
        </section>

        {/* ── HOW I WORK ── */}
        <section id="work" ref={addRef} className="fade-up pt-24 pb-4">
          <div className="max-w-4xl mx-auto px-6">
            <span className={eyebrow}>My Approach</span>
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-7">
                <h2 className={sectionTitle}>How I Work</h2>
                <p className="text-[#6b6b6b] leading-[1.8]">
                  I work holistically and person-centred, which means I don't
                  see you through the lens of your diagnosis or your
                  difficulties. I see you as a whole person with your own unique
                  history, your own strengths, your own way of making sense of
                  the world. My role is to walk alongside you, gently and at
                  your own pace, as you find your own way through.
                </p>
              </div>
              <div className="col-span-12 md:col-span-5 md:pl-8 md:border-l md:border-[#e8dcd9]">
                <span className="block text-[0.67rem] tracking-[0.25em] uppercase text-[#b07068] font-semibold mb-4">
                  I can support with
                </span>
                <ul className="list-none p-0 m-0 flex flex-col gap-[0.65rem]">
                  {supportItems.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-3 text-[#4a4a4a] text-[0.95rem]"
                    >
                      <span className="w-[6px] h-[6px] min-w-[6px] rounded-full bg-[#d9a299] inline-block" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── QUALIFICATIONS ── */}
        <section
          ref={addRef}
          className="fade-up bg-[rgba(240,228,211,0.4)] py-20 mt-20"
        >
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid grid-cols-12 gap-8 items-center">
              <div className="col-span-12 md:col-span-8">
                <span className={eyebrow}>Credentials</span>
                <h2 className={sectionTitle}>
                  Training & Professional Membership
                </h2>
                <p className="text-[#6b6b6b] leading-[1.8]">
                  I have a degree in Humanistic Counselling and Psychotherapy
                  and I am a registered member of the British Association for
                  Counselling and Psychotherapy (BACP). This means I follow
                  their Ethical Framework, which guides how counsellors work
                  safely and professionally with clients.
                </p>
              </div>
              <div className="col-span-12 md:col-span-4 flex justify-start md:justify-end items-center">
                <img
                  src={bapc}
                  alt="BACP Member"
                  style={{
                    maxHeight: "90px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── SERVICES ── */}
        <section id="services" ref={addRef} className="fade-up pt-24 pb-4">
          <div className="max-w-4xl mx-auto px-6">
            <span className={eyebrow}>Sessions</span>
            <h2 className={sectionTitle}>Services & Fees</h2>
            <p className="text-[#6b6b6b] leading-[1.8] max-w-[540px]">
              I offer individual therapy sessions tailored to your needs, a
              safe, supportive space to talk openly and work toward personal
              growth and healing.
            </p>

            <div className="rounded overflow-hidden bg-white shadow-[0_2px_24px_rgba(0,0,0,0.07)] mt-8">
              <div className="grid grid-cols-12">
                <div className="col-span-12 md:col-span-5 order-1 md:order-2">
                  <img
                    src={service}
                    alt="Therapy session"
                    className="w-full h-full object-cover block"
                    style={{ minHeight: "260px" }}
                  />
                </div>
                <div className="col-span-12 md:col-span-7 order-2 md:order-1 p-8 flex flex-col justify-center">
                  <div className="flex items-baseline justify-between mb-4">
                    <h3 className="font-lora text-xl font-bold text-[#3a3a3a]">
                      Online or Phone
                    </h3>
                    <span className="font-lora text-[2.2rem] font-bold text-[#b07068] leading-none">
                      £55
                    </span>
                  </div>
                  <p className="text-[#6b6b6b] text-sm leading-[1.8] mb-5">
                    Each session lasts 50 minutes and is conducted via Zoom or
                    phone. Please choose a quiet, private space where you can
                    speak freely and focus on the conversation.
                  </p>
                  <ul className="list-none p-0 m-0 flex flex-col gap-2 mb-7">
                    {[
                      "50-minute session via Zoom or phone",
                      "Safe, confidential space to talk freely",
                      "Support tailored to your needs",
                      "Guidance for wellbeing and personal growth",
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-2 text-sm text-[#5a5a5a]"
                      >
                        <svg
                          className="w-4 h-4 text-[#d9a299] flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div>
                    <button
                      type="button"
                      className={btn}
                      onClick={() => setModalOpen(true)}
                    >
                      Book Your Appointment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section
          id="testimonials"
          ref={addRef}
          className="fade-up bg-[rgba(240,228,211,0.4)] py-20 mt-20"
        >
          <div className="max-w-4xl mx-auto px-6">
            <span className={eyebrow}>Client Experiences</span>
            <h2 className={sectionTitle}>What Clients Say</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
              {testimonials.map((quote, i) => (
                <div
                  key={i}
                  className="p-7 border-t-2 border-[#d9a299] bg-[rgba(217,162,153,0.06)] rounded-[2px]"
                >
                  <div className="font-lora text-[3rem] leading-[0.75] text-[#d9a299] mb-4">
                    &ldquo;
                  </div>
                  <p className="text-[#5a5a5a] leading-[1.85] text-sm m-0">
                    {quote}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQs ── */}
        <section id="faqs" ref={addRef} className="fade-up pt-24 pb-4">
          <div className="max-w-4xl mx-auto px-6">
            <span className={eyebrow}>Questions</span>
            <h2 className={sectionTitle}>Frequently Asked Questions</h2>
            <div className="mt-6">
              {faqs.map(({ id, q, a }, idx) => (
                <div
                  key={id}
                  className={`border-b border-[#e8dcd9]${idx === 0 ? " border-t" : ""}`}
                >
                  <button
                    type="button"
                    className="w-full flex justify-between items-center py-5 bg-transparent border-none cursor-pointer font-lora font-semibold text-[0.98rem] text-left gap-4"
                    style={{ color: openFaq === id ? "#b07068" : "#3a3a3a" }}
                    onClick={() => setOpenFaq(openFaq === id ? null : id)}
                  >
                    {q}
                    <svg
                      className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${openFaq === id ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {openFaq === id && (
                    <div className="pb-5 text-[#6b6b6b] leading-[1.8] text-[0.95rem]">
                      {a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CONTACT ── */}
        <section
          id="contact"
          ref={addRef}
          className="fade-up bg-[rgba(240,228,211,0.4)] py-20 mt-20"
        >
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid grid-cols-12 gap-8 md:gap-16">
              <div className="col-span-12 md:col-span-4">
                <span className={eyebrow}>Get in Touch</span>
                <h2 className={sectionTitle}>Contact</h2>
                <p className="text-[#6b6b6b] leading-[1.8] text-sm">
                  I offer a free 20-minute introductory call to discuss your
                  needs and see if my approach is right for you. There's no
                  obligation. Just a warm conversation.
                </p>
              </div>
              <div className="col-span-12 md:col-span-8">
                <form onSubmit={handleSubmit}>
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <div className="flex-1 min-w-0">
                      <label className={labelCls}>Name</label>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="Your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className={labelCls}>Email</label>
                      <input
                        type="email"
                        className={inputCls}
                        placeholder="Your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className={labelCls}>Message</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      placeholder="How can I help you?"
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-6">
                    <label className={labelCls}>
                      Where did you hear about me?
                    </label>
                    <div className="flex flex-wrap gap-x-6 gap-y-3">
                      {hearAboutOptions.map((option) => (
                        <label
                          key={option}
                          className="flex items-center gap-2 text-[0.9rem] text-[#4a4a4a] cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-[#d9a299] cursor-pointer"
                            checked={hearAbout.includes(option)}
                            onChange={() => toggleHearAbout(option)}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    {hearAboutError && (
                      <p className="text-red-600 text-xs mt-2 mb-0">
                        Please select at least one option.
                      </p>
                    )}
                  </div>
                  <button type="submit" className={`${btn} w-full sm:w-auto`}>
                    Send Message
                  </button>
                  {status === "success" && (
                    <div className="bg-green-50 border border-green-300 text-green-800 rounded p-3 mt-4 text-sm">
                      Your message has been sent successfully!
                    </div>
                  )}
                  {status === "error" && (
                    <div className="bg-red-50 border border-red-300 text-red-800 rounded p-3 mt-4 text-sm">
                      Failed to send message. Please try again.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* ── BOOKING MODAL ── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modalTitle"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setModalOpen(false)}
          />
          <div className="relative bg-white rounded shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-[#e8dcd9]">
              <h5
                className="font-lora font-semibold text-[1.05rem] text-[#3a3a3a]"
                id="modalTitle"
              >
                Contact Me to Book
              </h5>
              <button
                type="button"
                className="text-[#9a9a9a] hover:text-[#3a3a3a] bg-transparent border-0 text-2xl cursor-pointer leading-none"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <p className="text-[#6b6b6b] text-sm leading-[1.8] mb-5">
                Thank you for your interest in my therapy sessions. Please
                contact me to schedule your appointment.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+447826804677" className={btn}>
                  Call Me
                </a>
                <a
                  href="https://wa.me/447826804677?text=Hello%2C%20I%20would%20like%20to%20book%20an%20appointment."
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btn}
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
